import React, { useState } from 'react';
import { X, Mail, Send, User, FileText } from 'lucide-react';
import apiClient from '../../api/config';
import { useToastContext } from '../common/Toast';
import { handleApiError } from '../../utils/errorHandler';
import { Invoice } from '../../types/invoice';

interface SendInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice;
  onSuccess?: () => void;
}

const SendInvoiceModal: React.FC<SendInvoiceModalProps> = ({
  isOpen,
  onClose,
  invoice,
  onSuccess
}) => {
  const { showSuccess, showError } = useToastContext();
  const [loading, setLoading] = useState(false);
  const [emailData, setEmailData] = useState({
    to_email: invoice.customer?.email || '',
    cc_emails: '',
    subject: `Invoice ${invoice.invoice_number} from Rayyi7`,
    message: `Dear ${invoice.customer?.name || 'Customer'},

Please find attached your invoice #${invoice.invoice_number}.

Invoice Details:
- Invoice Number: ${invoice.invoice_number}
- Amount: ${invoice.total_amount} ${invoice.currency || 'KWD'}
- Due Date: ${invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}

Thank you for your business.

Best regards,
Rayyi7 Team`
  });

  const handleSendInvoice = async () => {
    if (!emailData.to_email) {
      showError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post(`/invoices/${invoice.id}/send`, {
        to_email: emailData.to_email,
        cc_emails: emailData.cc_emails.split(',').map(email => email.trim()).filter(email => email),
        subject: emailData.subject,
        message: emailData.message,
        include_pdf: true
      });

      showSuccess('Invoice sent successfully');
      
      if (onSuccess) {
        onSuccess();
      }
      
      handleClose();
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmailData({
      to_email: invoice.customer?.email || '',
      cc_emails: '',
      subject: `Invoice ${invoice.invoice_number} from Rayyi7`,
      message: `Dear ${invoice.customer?.name || 'Customer'},

Please find attached your invoice #${invoice.invoice_number}.

Invoice Details:
- Invoice Number: ${invoice.invoice_number}
- Amount: ${invoice.total_amount} ${invoice.currency || 'KWD'}
- Due Date: ${invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}

Thank you for your business.

Best regards,
Rayyi7 Team`
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center">
              <Mail className="h-5 w-5 text-teal-600 mr-2" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Send Invoice via Email
                </h3>
                <p className="text-sm text-gray-600">
                  Invoice #{invoice.invoice_number}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Invoice Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <FileText className="h-8 w-8 text-teal-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Invoice #{invoice.invoice_number}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {invoice.total_amount} {invoice.currency || 'KWD'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center text-sm text-gray-600">
                    <User className="h-4 w-4 mr-1" />
                    {invoice.customer?.name || 'N/A'}
                  </div>
                  <p className="text-sm text-gray-500">
                    {invoice.customer?.phone || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Email Form */}
            <div className="space-y-4">
              {/* To Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={emailData.to_email}
                  onChange={(e) => setEmailData({ ...emailData, to_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="customer@example.com"
                  required
                />
              </div>

              {/* CC Emails */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CC Emails (optional)
                </label>
                <input
                  type="text"
                  value={emailData.cc_emails}
                  onChange={(e) => setEmailData({ ...emailData, cc_emails: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="email1@example.com, email2@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Separate multiple emails with commas
                </p>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={emailData.message}
                  onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  rows={8}
                  required
                />
              </div>

              {/* Options */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 text-blue-600 mr-2" />
                  <span className="text-sm text-blue-900">
                    Invoice PDF will be attached automatically
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSendInvoice}
              className="px-4 py-2 text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 transition-colors flex items-center"
              disabled={loading || !emailData.to_email || !emailData.subject || !emailData.message}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Invoice
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendInvoiceModal;